/**
 * Which language the application speaks.
 *
 * French by default, because this is a French financial reporting product and
 * most of the people using it work in French. English is offered alongside for
 * the groups and auditors who do not.
 *
 * The choice comes from three places, in order: what the person picked, what
 * their browser asks for, and French. Each is a better guess than the one after
 * it, and the first is the only one that is not a guess at all.
 */

import { createI18n } from 'vue-i18n'

import { en } from '@/i18n/en'
import { fr, type Messages } from '@/i18n/fr'

export type Locale = 'fr' | 'en'

export const LOCALES: readonly Locale[] = ['fr', 'en']

const STORAGE_KEY = 'dmbuilder.locale'
const FALLBACK: Locale = 'fr'

function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value)
}

/** What the person chose last time, if they chose. */
function remembered(): Locale | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored && isLocale(stored) ? stored : null
  } catch {
    // A browser set to block site data throws rather than returning null.
    return null
  }
}

/**
 * What the browser asks for.
 *
 * Read by prefix, so `fr-CA` and `fr-BE` both count as French. Matching the
 * whole tag would send a Belgian accountant to the English interface.
 */
function preferred(): Locale | null {
  for (const tag of navigator.languages ?? []) {
    const base = tag.toLowerCase().split('-')[0] ?? ''
    if (isLocale(base)) return base
  }
  return null
}

export function initialLocale(): Locale {
  return remembered() ?? preferred() ?? FALLBACK
}

/**
 * How French counts.
 *
 * Both languages here offer two forms, and the built-in rule sends anything
 * that is not 1 to the second. That is right for English and wrong for
 * French, which keeps the singular for zero: "0 tableau", not "0 tableaux".
 * Nowhere else in the interface would catch it, because every other count is
 * two or more by the time anybody sees it.
 */
function frenchPlural(choice: number, choicesLength: number): number {
  if (choicesLength < 2) return 0
  return choice <= 1 ? 0 : 1
}

export const i18n = createI18n<[Messages], Locale, false>({
  // The composition API, so components read messages through `useI18n()` and
  // the compiler can check the keys.
  legacy: false,
  locale: initialLocale(),
  fallbackLocale: FALLBACK,
  messages: { fr, en },
  pluralRules: { fr: frenchPlural },
  // A missing key is a bug, and in development it should be loud. In
  // production the fallback quietly covers it rather than showing a raw key
  // to somebody in the middle of signing a report.
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
})

/**
 * Translate, from outside a component.
 *
 * Components use `useI18n()`, which the compiler can check. A store runs
 * outside any component's setup and has no such handle, so it reads the global
 * one. Exported here rather than left to each store to reach into `i18n.global`
 * itself, which would put the same line in five files.
 *
 * Safe to hold onto: the locale lives in a ref this reads on each call, so a
 * message translated through it follows a language change like any other.
 */
export const t = i18n.global.t

/** The language in use. */
export function currentLocale(): Locale {
  return i18n.global.locale.value
}

/**
 * Change the language, and remember it.
 *
 * Also set on the document, so the browser hyphenates and quotes correctly.
 * A French document hyphenated by English rules breaks words in the wrong
 * places, which is visible in justified text at a glance.
 */
export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale
  document.documentElement.lang = locale

  try {
    window.localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // It still applies for this session.
  }
}
